import { useState, useEffect } from 'react'
import PubSub from 'pubsub-js'
import classNames from 'classnames'
import { publishEvent, makeInteger, publishEventSync, getHookInfo } from '../../utils'
import allHooks from '../../hooks/allHooks'
import { FormGroup, FormFeedback, Label } from 'reactstrap'
import { FIELD_TYPE, BEHAVIOR_TYPE, ACTION_TYPE, FIELD_ID } from '../../constants'
import AppObjectFieldText from './AppObjectFieldText'
import AppObjectFieldMultilineText from './AppObjectFieldMultilineText'
import AppObjectFieldDate from './AppObjectFieldDate'
import AppObjectFieldTime from './AppObjectFieldTime'
import AppObjectFieldSelect from './AppObjectFieldSelect'
import AppObjectFieldMultiSelect from './AppObjectFieldMultiSelect'
import AppObjectFieldYesNo from './AppObjectFieldYesNo'
import AppObjectFieldObjectId from './AppObjectFieldObjectId'
import AppObjectFieldObjectOwner from './AppObjectFieldObjectOwner'
import AppObjectFieldObjectState from './AppObjectFieldObjectState'
import AppObjectFieldStaticText from './AppObjectFieldStaticText'
import AppObjectFieldNumber from './AppObjectFieldNumber'
import AppObjectFieldCurrency from './AppObjectFieldCurrency'
import AppObjectFieldObjectType from './AppObjectFieldObjectType'
import AppObjectFieldImage from './AppObjectFieldImage'
import AppObjectFieldFile from './AppObjectFieldFile'
import AppObjectFieldHyperlink from './AppObjectFieldHyperlink'
import AppObjectFieldRefList from './AppObjectFieldRefList'
import AppObjectFieldFilterValue from './AppObjectFieldFilterValue'
import AppObjectFieldJournal from './AppObjectFieldJournal'
import './AppObjectField.css'


export default function AppObjectField(props) {
  const { object, parentObjectId, field, action, formInputs, showFieldLabel } = props
  console.log(`AppObjectField rendered: ${field.name}`, props)

  const [fieldValue, setFieldValue] = useState(formInputs[field.id].currentValue)
  const [refListChangeCount, setRefListChangeCount] = useState(0)
  // eslint-disable-next-line no-unused-vars
  const [refreshDependant, setRefreshDependant] = useState()
  // eslint-disable-next-line no-unused-vars
  const [behaviorType, setBehaviorType] = useState(field.behaviorType)

  // for fields that depend on other fields
  useEffect(() => {
    function handleDependsOnChangeEvent(msg, data) {
      console.log(`EVENT RECEIVED (AppObjectField): ${field.name} recieved ${msg} to `, data)
      for (let dependsOn of field.dependsOn) {
        // ignore dropdown re-select of current selected value
        if (data !== formInputs[dependsOn.id].previousValue) {
          console.log(`${field.name} dependsOn field changed, clear current value or set to defalut value `)
          if (makeInteger(field.id) === FIELD_ID.query.details && formInputs[dependsOn.id].previousValue.id !== null) {
            window.location.reload()
          }
          if (makeInteger(field.type.id) === FIELD_TYPE.referenceList) {
            formInputs[field.id].currentValue.objects = []
            for (let index in formInputs[field.id].currentValue) {
              if (!isNaN(parseInt(index))) {
                delete formInputs[field.id].currentValue[index]
              }
            }
            referenceListHandleChange(formInputs[field.id].currentValue, referenceListHasChanged, field.id)
            // only clears the array if the x is selected. does not clear ref list for selecting a different field
          }
          else if (makeInteger(field.type.id) === FIELD_TYPE.select) {
            selectHandleChange(null, selectHasChanged, field.id)
          }
          else if (makeInteger(field.type.id) === FIELD_TYPE.yesNo) {
            handleChange(field.defaultValue, simpleHasChanged, field.id)
          }
          else if (makeInteger(field.type.id) === FIELD_TYPE.filterValue) {
            handleChange(null, simpleHasChanged, field.id)
          }
          setRefreshDependant(data)
        }
      }
    }
    const tokens = []
    if (field.dependsOn) {
      for (let dependsOn of field.dependsOn) {
        tokens.push(PubSub.subscribe(`OBJECT: ${object.id} ${dependsOn.name} CHANGED`, handleDependsOnChangeEvent))
      }
      return function cleanup() {
        for (let token of tokens) {
          PubSub.unsubscribe(token)
        }
      }
    }
  })

  useEffect(() => {
    async function runBehaviorHook() {
      const hookInfo = getHookInfo(field.behaviorHook)
      const behaviorType = await allHooks[hookInfo.moduleName][hookInfo.functionName](object, field, action)
      field.behaviorType = behaviorType
      setBehaviorType(behaviorType)
    }
    if (action && field.behaviorHook) {
      console.log('----------------------- AppObjectField.useEffect[] BEHAVIOR HOOK: ', field.name, field.behaviorType, field.behaviorHook)
      runBehaviorHook()
    }
    formInputs[field.id].previousValue = formInputs[field.id].currentValue
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action])

  function multiSelectHasChanged(savedValue, currentValue) {
    if (currentValue.length !== savedValue.length) {
      return true
    } else {
      for (let i = 0; i < currentValue.length; i++) {
        if (currentValue[i].id !== savedValue[i].id) {
          return true
        }
      }
    }
    return false
  }

  function selectHasChanged(savedValue, currentValue) {
    // currentValue is null
    if (!currentValue) {
      // savedValue is not null
      if (savedValue.id) {
        return true
      }
    } else {
      // currentValue is not null
      if (!savedValue) {
        // savedValue is null
        return true
      } else {
        // savedValue is not null
        if (savedValue.id !== currentValue.id) {
          return true
        }
      }
    }
    return false
  }

  function simpleHasChanged(savedValue, currentValue) {
    console.log('AppObjectField.simpleHasChanged: ', { savedValue, currentValue }, (savedValue !== currentValue))
    // eslint-disable-next-line
    if (savedValue !== currentValue) {
      return true
    } else {
      return false
    }
  }

  function referenceListHasChanged(savedValue, currentValue) {
    console.log('AppObjectField.referenceListHasChanged', savedValue, currentValue)
    if (!savedValue.gridData && !currentValue.gridData) {
      return false
    }

    else if (savedValue.gridData.length !== currentValue.gridData.length) {
      return true
    }
    return false
  }

  function handleChange(value, hasChanged, fieldId) {
    console.log('AppObjectField.handleChange: ', { value, fieldId })
    const savedValue = formInputs[fieldId].savedValue
    const previouslyChanged = formInputs[fieldId].changed
    const changed = hasChanged(savedValue, value)

    formInputs[fieldId].changed = changed
    formInputs[fieldId].previousValue = formInputs[fieldId].currentValue
    formInputs[fieldId].currentValue = value

    if (changed && !previouslyChanged) {
      // if the field value has changed and the field value was previously unchanged
      // increment the change count
      const eventData = 1
      publishEvent(`CHANGE COUNT ${object.id}`, eventData, 'AppObjectField.handleChange')
    } else if (!changed && previouslyChanged) {
      // if the field value has NOT changed and the field value was previously changed
      // decrement the change count
      const eventData = -1
      publishEvent(`CHANGE COUNT ${object.id}`, eventData, 'AppObjectField.handleChange')
    }

    // notify any dependent fields of the change
    if (field.hasDependents === 'Y' && (formInputs[fieldId].previousValue !== formInputs[fieldId].currentValue || makeInteger(action.type.id) === ACTION_TYPE.submit)) {
      publishEventSync(`OBJECT: ${object.id} ${field.name} CHANGED`, value, 'AppObjectField.handleChange')
    }

    // otherwise trigger field re-render
    setFieldValue(value)
    if (makeInteger(field.type.id) === FIELD_TYPE.referenceList) {
      setRefListChangeCount(refListChangeCount + 1)
    }
  }

  function simpleHandleChange(event, hasChanged) {
    console.log('AppObjectField simpleHandleChange: ', field, {fieldId: event.target.id, length: event.target.value.length, fieldMaxLength: field.maxLength})
    //console.log(field)
    if (makeInteger(field.type.id) === FIELD_TYPE.shortText && event.target.value.length > field.maxLength) {
      console.log('************************ OVER MAX')
      return false
    }
    handleChange(event.target.value, hasChanged, event.target.id)
  }

  function currencyHandleChange(event, hasChanged) {
    console.log(`AppObjectField.currencyHandleChange: fieldId: ${event.target.id} value: ${event.target.value}`, {event})
    let newValue = event.target.value
    if (event.target.value) {
      newValue = parseFloat(event.target.value.replace(/,/g, ''))
    }
    handleChange(newValue, hasChanged, event.target.id)
  }

  function selectHandleChange(selectedValue, hasChanged, fieldId) {
    console.log('selectHandleChange value: ', selectedValue)
    let newValue = null
    if (selectedValue) {
      newValue = {
        id: selectedValue.value,
        name: selectedValue.label
      }
    }
    handleChange(newValue, hasChanged, fieldId)
  }

  function multiSelectHandleChange(selectedValues, hasChanged, fieldId) {
    console.log('multiSelectHandleChange value: ', selectedValues)
    let newValues = []
    if (selectedValues) {
      selectedValues.forEach((selectedValue) => {
        newValues.push({ id: selectedValue.value, name: selectedValue.label })
      })
    }
    handleChange(newValues, hasChanged, fieldId)
  }

  function referenceListHandleChange(value, hasChanged, fieldId) {
    console.log('AppObjectField.referenceListHandleChange', value, fieldId)
    if (makeInteger(fieldId) === FIELD_ID.query.details) {
      PubSub.publish('QUERY DETAILS DOCUMENT TYPE CHANGES')
    } else {
      handleChange(value, hasChanged, fieldId)
    }
  }

  function isRequired() {
    if ((makeInteger(field.behaviorType.id) === BEHAVIOR_TYPE.required) && (action && makeInteger(action.type.id) !== ACTION_TYPE.delete)) {
      return true
    }
    return false
  }

  let showLabel = showFieldLabel === 'Y' ? true : false
  let fieldComponent = null
  let inputAttribute = ''
  let formFeedback = ''

  // FIELD VALIDATION
  if (formInputs[field.id].validationMessage) {
    inputAttribute = 'invalid'
    formFeedback = <FormFeedback className='form-feedback-invalid'>{formInputs[field.id].validationMessage}</FormFeedback>
  }

  switch (parseInt(field.type.id, 10)) {
    case FIELD_TYPE.objectId:
      fieldComponent = <AppObjectFieldObjectId field={field} value={fieldValue} />
      break
    case FIELD_TYPE.objectOwner:
      fieldComponent = <AppObjectFieldObjectOwner field={field} value={fieldValue.name} />
      break
    case FIELD_TYPE.objectState:
      fieldComponent = <AppObjectFieldObjectState field={field} value={fieldValue.name} />
      break
    case FIELD_TYPE.objectType:
      fieldComponent = <AppObjectFieldObjectType field={field} value={fieldValue.name} />
      break
    case FIELD_TYPE.shortText:
      fieldComponent =
        <AppObjectFieldText
          field={field}
          action={action}
          value={fieldValue}
          handleChange={simpleHandleChange}
          hasChanged={simpleHasChanged}
          inputAttribute={inputAttribute}
        />
      break
    case FIELD_TYPE.number:
    case FIELD_TYPE.integer:
      fieldComponent =
        <AppObjectFieldNumber
          field={field}
          action={action}
          value={fieldValue}
          handleChange={simpleHandleChange}
          hasChanged={simpleHasChanged}
          inputAttribute={inputAttribute}
        />
      break
    case FIELD_TYPE.currency:
      fieldComponent =
        <AppObjectFieldCurrency
          field={field}
          action={action}
          value={fieldValue}
          handleChange={currencyHandleChange}
          hasChanged={simpleHasChanged}
        />
      break
    case FIELD_TYPE.yesNo:
      fieldComponent =
        <AppObjectFieldYesNo
          field={field}
          action={action}
          value={fieldValue}
          handleChange={simpleHandleChange}
          hasChanged={simpleHasChanged}
        />
      break
    case FIELD_TYPE.multilineText:
      fieldComponent =
        <AppObjectFieldMultilineText
          field={field}
          action={action}
          value={fieldValue}
          handleChange={simpleHandleChange}
          hasChanged={simpleHasChanged}
        />
      break
    case FIELD_TYPE.date:
      fieldComponent =
        <AppObjectFieldDate
          field={field}
          action={action}
          value={fieldValue}
          handleChange={simpleHandleChange}
          hasChanged={simpleHasChanged}
        />
      break
    case FIELD_TYPE.time:
      fieldComponent =
        <AppObjectFieldTime
          field={field}
          action={action}
          value={fieldValue}
          handleChange={simpleHandleChange}
          hasChanged={simpleHasChanged}
        />
      break
    case FIELD_TYPE.multiSelect:
      fieldComponent =
        <AppObjectFieldMultiSelect
          field={field}
          object={object}
          formInputs={formInputs}
          parentObjectId={parentObjectId}
          action={action}
          value={fieldValue}
          handleChange={multiSelectHandleChange}
          hasChanged={multiSelectHasChanged}
          inputAttribute={inputAttribute}
        />
      break
    case FIELD_TYPE.filterValue:
      fieldComponent =
        <AppObjectFieldFilterValue
          field={field}
          object={object}
          formInputs={formInputs}
          parentObjectId={parentObjectId}
          action={action}
          value={fieldValue}
          handleChange={simpleHandleChange}
          hasChanged={simpleHasChanged}
          multiHandleChange={multiSelectHandleChange}
          multiHasChanged={multiSelectHasChanged}
          inputAttribute={inputAttribute}
        />
      break
    case FIELD_TYPE.select:
      fieldComponent =
        <AppObjectFieldSelect
          field={field}
          object={object}
          formInputs={formInputs}
          parentObjectId={parentObjectId}
          action={action}
          value={fieldValue}
          handleChange={selectHandleChange}
          hasChanged={selectHasChanged}
          inputAttribute={inputAttribute}
        />
      break
    case FIELD_TYPE.image:
      fieldComponent =
        <AppObjectFieldImage
          objectId={object.id}
          field={field}
          action={action}
          value={fieldValue}
          formInputs={formInputs}
          handleChange={handleChange}
          hasChanged={simpleHasChanged}
        />
      break
    case FIELD_TYPE.file:
      fieldComponent =
        <AppObjectFieldFile
          object={object}
          field={field}
          action={action}
          value={fieldValue}
          formInputs={formInputs}
          handleChange={handleChange}
          hasChanged={simpleHasChanged}
        />
      break
    case FIELD_TYPE.hyperlink:
      fieldComponent =
        <AppObjectFieldHyperlink
          field={field}
          action={action}
          value={fieldValue}
          handleChange={simpleHandleChange}
          hasChanged={simpleHasChanged}
        />
      break
    case FIELD_TYPE.staticText:
      fieldComponent =
        <AppObjectFieldStaticText
          field={field}
          action={action}
          value={fieldValue}
        />
      showLabel = false
      break
    case FIELD_TYPE.referenceList:
      fieldComponent =
        <AppObjectFieldRefList
          formInputs={formInputs[field.id]}
          object={object}
          parentObjectId={parentObjectId}
          field={field}
          action={action}
        />
      break
    case FIELD_TYPE.journal:
      fieldComponent =
        <AppObjectFieldJournal
          objectId={object.id}
          field={field}
          action={action}
          value={fieldValue}
          handleChange={simpleHandleChange}
          hasChanged={simpleHasChanged}
        />
      break
    default:
      fieldComponent = <p>Field type "{field.type.name}" is not yet implemented.</p>
  }

  let renderControl = true
  let comma = ''
  let dependsOnText = ''
  if (field.dependsOn) {
    for (let parentField of field.dependsOn) {
      let parentFieldHasValue = true
      dependsOnText += comma + parentField.name
      if (makeInteger(parentField.type.id) === FIELD_TYPE.yesNo) {
        parentFieldHasValue = checkYesNo(parentField)
      } else if (!formInputs[parentField.id].currentValue) {
        parentFieldHasValue = false
      } else if (formInputs[parentField.id].currentValue) {
        if (!formInputs[parentField.id].currentValue.id) {
          parentFieldHasValue = false
        }
      }
      if (!parentFieldHasValue) {
        renderControl = false
      }
      comma = ', '
    }
  }

  function checkYesNo(parentField) {
    switch (makeInteger(field.id)) {
      case FIELD_ID.queryDetail.notOperator:
      case FIELD_ID.queryDetail.operator:
      case FIELD_ID.queryDetail.filterValue:
        console.log(formInputs[parentField.id].currentValue)
        if (formInputs[parentField.id].currentValue === 'Y') {
          return true
        }
        break
      default:
    }
    return false
  }

  if (!renderControl && action) {
    // happens after clicking x on document type
    fieldComponent = <div data-cy='query-detail-field-component'>Depends on {dependsOnText}</div>
    formInputs[field.id].currentValue = {}
    formInputs[field.id].previousValue = {}
    formInputs[field.id].savedValue = {}
    formInputs[field.id].changed = false
  }

  let changed = formInputs[field.id].changed

  // Special logic to handle Yes/No fields with default values
  if (action && makeInteger(action.type.id) === ACTION_TYPE.submit && makeInteger(field.type.id) === FIELD_TYPE.yesNo) {
    if (field.defaultValue === formInputs[field.id].currentValue) {
      changed = false
    }
  }

  return (
    <FormGroup className={classNames('app-object-field-form-group', { 'changed': changed })}>
      {showLabel ? <Label className="app-object-field-label" for={`${field.id}`}>{isRequired() ? <span className="red-text">* </span> : ''}{field.name}</Label> : ''}
      {fieldComponent}
      {formFeedback}
    </FormGroup>
  )
}
